'use client';

import Image from 'next/image';
import { Container } from 'react-bootstrap';
import type { PortableTextBlock } from 'next-sanity';
import DotLottieReact from '@/componentv2/dotLottie/dotLottie';

import Button from '@/componentv2/button/button';
import FontAwesomeIcon from '@/componentv2/fontAwesomeIcon/fontAwesomeIcon';
import { LandingPortableText } from '@/components/landingPortableText/landingPortableText';
import { toEdgeAssetUrl, urlForImage } from '@/sanity/lib/utils';
import type { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import { getSectionBackgroundStyle } from '../../utils';
import './HeroSection.scss';

type LandingPageData = Extract<
  NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>,
  { _type: 'landingPage' }
>;

interface HeroSectionProps {
  data: NonNullable<LandingPageData['hero']>;
}

export default function HeroSection({ data }: HeroSectionProps) {
  const {
    introLine,
    heroContent,
    ctaText,
    ctaLink,
    ctaSubtext,
    imageOptions,
    trustBadges,
    background,
  } = data;

  const heroContentBlocks = heroContent as PortableTextBlock[] | undefined;
  const headingBlocks = heroContentBlocks?.filter(
    (block) => block.style === 'h1' || block.style === 'h2'
  );
  const descriptionBlocks = heroContentBlocks?.filter(
    (block) => block.style !== 'h1' && block.style !== 'h2'
  );

  const trustLogoBadges = trustBadges?.badges;

  return (
    <section className="rlp-hero" style={getSectionBackgroundStyle(background)}>
      <Container>
        <div className="rlp-hero-content">
          <div className="rlp-hero-text">
            {introLine && <span className="rlp-hero-intro">{introLine}</span>}
            {headingBlocks && headingBlocks.length > 0 && (
              <div className="rlp-hero-headline">
                <LandingPortableText value={headingBlocks} type="hero" />
              </div>
            )}
          </div>

          {ctaText && (
            <div className="rlp-hero-cta">
              <Button href={ctaLink} variant="primary">
                {ctaText}
              </Button>
            </div>
          )}

          <div className="rlp-hero-image">
            {imageOptions?.type === 'staticImage' &&
              imageOptions?.heroImage && (
                <Image
                  src={urlForImage(imageOptions.heroImage)?.url() ?? ''}
                  alt={imageOptions.heroImage.alt || 'Hero image'}
                  width={600}
                  height={450}
                  priority
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            {imageOptions?.type === 'animatedHero' &&
              imageOptions?.heroAnimated?.fileUrl && (
                <DotLottieReact
                  src={toEdgeAssetUrl(imageOptions.heroAnimated.fileUrl)}
                  autoplay
                  loop
                />
              )}
          </div>

          {descriptionBlocks && descriptionBlocks.length > 0 && (
            <div className="rlp-hero-description rlp-hero-headline">
              <LandingPortableText value={descriptionBlocks} type="hero" />
            </div>
          )}

          {ctaSubtext && (
            <div className="rlp-hero-cta-subtext">
              <div className="rlp-hero-highlight">
                <FontAwesomeIcon
                  name="fa-caret-right"
                  style="fa-solid"
                  fontSize="16px"
                />
                <span>{ctaSubtext}</span>
              </div>
            </div>
          )}
        </div>

        {trustLogoBadges && trustLogoBadges.length > 0 && (
          <div className="rlp-hero-trust-badges">
            {trustBadges?.title && (
              <span className="rlp-hero-trust-title">{trustBadges.title}</span>
            )}
            <div className="rlp-hero-trust-logos">
              {trustLogoBadges.map((badge) => (
                <div className="rlp-hero-trust-logo" key={badge._key}>
                  <Image
                    src={
                      badge.logo ? (urlForImage(badge.logo)?.url() ?? '') : ''
                    }
                    alt={badge.altText || ''}
                    width={160}
                    height={60}
                  />
                </div>
              ))}
            </div>

            <div className="rlp-hero-trust-logos-marquee">
              <div className="rlp-hero-trust-logos rlp-hero-trust-logos--marquee">
                {[...trustLogoBadges, ...trustLogoBadges].map(
                  (badge, index) => {
                    const isDuplicate = index >= trustLogoBadges.length;
                    return (
                      <div
                        className="rlp-hero-trust-logo"
                        key={`${badge._key}-${index}`}
                        aria-hidden={isDuplicate}
                      >
                        <Image
                          src={
                            badge.logo
                              ? (urlForImage(badge.logo)?.url() ?? '')
                              : ''
                          }
                          alt={isDuplicate ? '' : badge.altText || ''}
                          width={160}
                          height={60}
                        />
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        )}
      </Container>
    </section>
  );
}
